import React, { useState, useCallback } from 'react';
import CollapsiblePanel from './CollapsiblePanel';
import { FiChevronDown, FiChevronRight, FiEdit2, FiPlus, FiFolderPlus, FiArrowUp, FiArrowDown, FiTrash2, FiSquare } from 'react-icons/fi';
import { useDroppable } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { useEditing } from './EditingContext';
import EditStyleDialog from './components/EditStyleDialog';

// Zentrale Opazität für alle Panels (0.0 = komplett transparent, 1.0 = deckend)
const PANEL_OPACITY = 0.2;

// Hilfsfunktion um eine beliebige Farbe (hex, rgb, rgba) mit definierter Opazität zurückzugeben
const applyOpacityToColor = (color, opacity) => {
  if (!color) return `rgba(255, 255, 255, ${opacity})`;

  // Wenn bereits rgba, nur die Alpha-Komponente ersetzen
  if (color.startsWith('rgba')) {
    return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, `rgba($1, $2, $3, ${opacity})`);
  }

  // Wenn rgb, zu rgba erweitern
  if (color.startsWith('rgb')) {
    return color.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/, `rgba($1, $2, $3, ${opacity})`);
  }

  // Wenn Hex, zu rgba konvertieren
  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  return `rgba(255, 255, 255, ${opacity})`;
};

// Draggable Panel-Komponente
function DraggablePanel({ panel, isAuthor, children }) {
  const { showEditButtons, isEditing } = useEditing();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: panel.id,
    disabled: !isAuthor || !showEditButtons || isEditing
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
}

// Draggable PanelGroup-Komponente
function DraggablePanelGroup({ panel, isAuthor, children }) {
  const { showEditButtons, isEditing } = useEditing();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: panel.id,
    disabled: !isAuthor || !showEditButtons || isEditing
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
}

// Droppable Group-Komponente
function DroppableGroup({ groupId, isAuthor, children, isEmpty = false }) {
  const { showEditButtons, isEditing } = useEditing();
  const { isOver, setNodeRef } = useDroppable({
    id: groupId,
    disabled: !isAuthor || !showEditButtons || isEditing
  });

  const style = {
    backgroundColor: isOver ? '#e8f5e8' : undefined,
    border: isOver ? '2px dashed #4caf50' : undefined,
    borderRadius: isOver ? '8px' : undefined,
    transition: 'all 0.2s ease'
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
}

export default function PanelGroup({
  groupTitle,
  onTitleChange,
  panelsInit = [],
  onPanelsChange,
  isAuthor,
  groupId,
  textColor = '#000000',
  backgroundColor = 'rgba(253, 255, 229, 0.9)',
  borderStyle = 'solid',
  borderColor = '#bbb',
  borderThickness = '1',
  onSave,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onDelete,
  open = true,
  onOpenChange,
  visibleWeeks = 10,
  weekWidth = 200,
  isVisibleWeeksChanging = false,
  isZoomChanging = false,
  onInfoPanelsChange,
  batchOperations,
  onCheckForDuplicates
}) {
  const [panels, setPanels] = useState(panelsInit);
  const [showPanelDialog, setShowPanelDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newPanelTitle, setNewPanelTitle] = useState('');
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [isOpen, setIsOpen] = useState(open);

  // Sync open state with prop
  React.useEffect(() => {
    setIsOpen(open);
  }, [open]);

  // Editierbarer Gruppen-Titel und Farben
  const [editing, setEditing] = useState(false);
  const [editGroupTitle, setEditGroupTitle] = useState(groupTitle);
  const [editTextColor, setEditTextColor] = useState(textColor);
  const [editBackgroundColor, setEditBackgroundColor] = useState(backgroundColor);
  const [editBorderStyle, setEditBorderStyle] = useState(borderStyle);
  const [editBorderColor, setEditBorderColor] = useState(borderColor);
  const [editBorderThickness, setEditBorderThickness] = useState(borderThickness);

  // Globaler Editing-Status
  const { showEditButtons, setIsEditing } = useEditing();

  // Alle Task-IDs für diese PanelGroup sammeln
  const getAllGroupTaskIds = () => {
    const taskIds = [];
    panels.forEach(panel => {
      if (panel.type === 'panel' && panel.movableBars) {
        panel.movableBars.forEach(bar => {
          taskIds.push(`${panel.id}_${bar.id}`);
        });
      } else if (panel.type === 'group' && panel.panels) {
        // Rekursiv für verschachtelte Gruppen
        panel.panels.forEach(subPanel => {
          if (subPanel.movableBars) {
            subPanel.movableBars.forEach(bar => {
              taskIds.push(`${subPanel.id}_${bar.id}`);
            });
          }
        });
      }
    });
    return taskIds;
  };

  // Toggle für alle PanelGroup-Tasks
  const handleToggleAllGroupTasks = (e) => {
    e.stopPropagation();
    if (batchOperations && batchOperations.togglePanelTasks) {
      const allTaskIds = getAllGroupTaskIds();
      batchOperations.togglePanelTasks(allTaskIds);
    }
  };



  // Stable callback for updating bars
  const handleBarsChange = useCallback((panelId, newBars) => {
    setPanels(currentPanels => {
      const updatedPanels = currentPanels.map(p =>
        p.id === panelId 
          ? { ...p, movableBars: newBars }
          : p
      );
      // Rufe onPanelsChange asynchron auf um React Render-Zyklus-Konflikte zu vermeiden
      if (onPanelsChange) {
        setTimeout(() => onPanelsChange(updatedPanels), 0);
      }
      return updatedPanels;
    });
  }, [onPanelsChange]);

  React.useEffect(() => {
    setPanels(panelsInit);
  }, [panelsInit]);

  // Sync state with props when they change
  React.useEffect(() => {
    setEditGroupTitle(groupTitle);
    setEditTextColor(textColor);
    setEditBackgroundColor(backgroundColor);
    setEditBorderStyle(borderStyle);
    setEditBorderColor(borderColor);
    setEditBorderThickness(borderThickness);
  }, [groupTitle, textColor, backgroundColor, borderStyle, borderColor, borderThickness]);

  // Panel hinzufügen
  const handleAddPanel = () => {
    if (newPanelTitle.trim() === '') return;
    const newPanels = [
      ...panels,
      { 
        id: Date.now().toString(), 
        type: 'panel', 
        title: newPanelTitle,
        textColor: '#000000',
        backgroundColor: 'rgba(253, 255, 229, 0.9)',
        borderStyle: 'solid',
        borderColor: '#ddd',
        borderThickness: '1',
        movableBars: []
      }
    ];
    setPanels(newPanels);
    setNewPanelTitle('');
    setShowPanelDialog(false);
    if (onPanelsChange) onPanelsChange(newPanels);
    
    // Prüfe nach dem Hinzufügen auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  // PanelGroup hinzufügen
  const handleAddGroup = () => {
    if (newGroupTitle.trim() === '') return;
    const newPanels = [
      ...panels,
      {
        id: (Date.now() + Math.random()).toString(),
        type: 'group',
        title: newGroupTitle,
        textColor: '#000000',
        backgroundColor: 'rgba(253, 255, 229, 0.9)',
        borderStyle: 'solid',
        borderColor: '#bbb',
        borderThickness: '1',
        panels: []
      }
    ];
    setPanels(newPanels);
    setNewGroupTitle('');
    setShowGroupDialog(false);
    if (onPanelsChange) onPanelsChange(newPanels);
    
    // Prüfe nach dem Hinzufügen auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setIsEditing(true);
  };
  const handleCancel = () => {
    setEditGroupTitle(groupTitle);
    setEditTextColor(textColor);
    setEditBackgroundColor(backgroundColor);
    setEditBorderStyle(borderStyle);
    setEditBorderColor(borderColor);
    setEditBorderThickness(borderThickness);
    setEditing(false);
    setIsEditing(false);
  };
  const handleSave = () => {
    setEditing(false);
    setIsEditing(false);
    if (onSave) {
      // Erzwinge die zentrale Opazität auf die Hintergrundfarbe
      const backgroundColorWithOpacity = applyOpacityToColor(editBackgroundColor, PANEL_OPACITY);
      onSave(editGroupTitle, editTextColor, backgroundColorWithOpacity, editBorderStyle, editBorderColor, editBorderThickness);
    } else if (onTitleChange) {
      onTitleChange(editGroupTitle);
    }
    
    // Prüfe nach dem Umbenennen auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    setShowDeleteDialog(false);
    setEditing(false);
    setIsEditing(false);
    if (onDelete) {
      onDelete();
    }
    
    // Prüfe nach dem Löschen auf Duplikate
    if (onCheckForDuplicates) {
      setTimeout(onCheckForDuplicates, 100);
    }
  };

  // Panel oder PanelGroup rendern
  const renderPanel = (panel, index) => {
    const canMoveUp = index > 0;
    const canMoveDown = index < panels.length - 1;

    const handleMoveUp = () => {
      if (canMoveUp) {
        const newPanels = [...panels];
        [newPanels[index], newPanels[index - 1]] = [newPanels[index - 1], newPanels[index]];
        setPanels(newPanels);
        if (onPanelsChange) onPanelsChange(newPanels);
      }
    };

    const handleMoveDown = () => {
      if (canMoveDown) {
        const newPanels = [...panels];
        [newPanels[index], newPanels[index + 1]] = [newPanels[index + 1], newPanels[index]];
        setPanels(newPanels);
        if (onPanelsChange) onPanelsChange(newPanels);
      }
    };

    if (panel.type === 'group') {
      return (
        <DraggablePanelGroup key={panel._reactKey || panel.id} panel={panel} isAuthor={isAuthor}>
          <div style={{ position: 'relative' }}>
            <PanelGroup
              groupTitle={panel.title}
              textColor={panel.textColor || '#000000'}
              backgroundColor={panel.backgroundColor || '#f8f9fa'}
              borderStyle={panel.borderStyle || 'solid'}
              borderColor={panel.borderColor || '#bbb'}
              borderThickness={panel.borderThickness || '1'}
              open={panel.open !== undefined ? panel.open : true}
              onOpenChange={(newOpen) => {
                const newPanels = panels.map(p =>
                  p.id === panel.id ? { ...p, open: newOpen } : p
                );
                setPanels(newPanels);
                if (onPanelsChange) onPanelsChange(newPanels);
              }}
              panelsInit={panel.panels}
              isAuthor={isAuthor}
              groupId={panel.id}
              visibleWeeks={visibleWeeks}
              weekWidth={weekWidth}
              isVisibleWeeksChanging={isVisibleWeeksChanging}
              batchOperations={batchOperations}
              onCheckForDuplicates={onCheckForDuplicates}
              onSave={(newTitle, newTextColor, newBackgroundColor, newBorderStyle, newBorderColor, newBorderThickness) => {
                // Titel, Farben und Border der Kindgruppe ändern
                const newPanels = panels.map(p =>
                  p.id === panel.id 
                    ? { ...p, title: newTitle, textColor: newTextColor, backgroundColor: newBackgroundColor, borderStyle: newBorderStyle, borderColor: newBorderColor, borderThickness: newBorderThickness } 
                    : p
                );
                setPanels(newPanels);
                if (onPanelsChange) onPanelsChange(newPanels);
                
                // Prüfe nach dem Umbenennen auf Duplikate
                if (onCheckForDuplicates) {
                  setTimeout(onCheckForDuplicates, 100);
                }
              }}
              onTitleChange={newTitle => {
                // Fallback für alte onTitleChange-Aufrufe
                const newPanels = panels.map(p =>
                  p.id === panel.id ? { ...p, title: newTitle } : p
                );
                setPanels(newPanels);
                if (onPanelsChange) onPanelsChange(newPanels);
                
                // Prüfe nach dem Umbenennen auf Duplikate
                if (onCheckForDuplicates) {
                  setTimeout(onCheckForDuplicates, 100);
                }
              }}
              onPanelsChange={newPanels => {
                // Panels der Kindgruppe ändern
                const updatedPanels = panels.map(p =>
                  p.id === panel.id ? { ...p, panels: newPanels } : p
                );
                setPanels(updatedPanels);
                if (onPanelsChange) onPanelsChange(updatedPanels);
              }}
              onDelete={() => {
                // PanelGroup löschen
                const newPanels = panels.filter(p => p.id !== panel.id);
                setPanels(newPanels);
                if (onPanelsChange) onPanelsChange(newPanels);
                
                // Prüfe nach dem Löschen auf Duplikate
                if (onCheckForDuplicates) {
                  setTimeout(onCheckForDuplicates, 100);
                }
              }}
            />
            {isAuthor && showEditButtons && (canMoveUp || canMoveDown) && (
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '60px',
                display: 'flex',
                gap: '4px',
                zIndex: 10
              }}>
                {canMoveUp && (
                  <button
                    style={{
                      background: 'rgba(255,255,255,0.9)',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      padding: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#666'
                    }}
                    onClick={handleMoveUp}
                    title="Gruppe nach oben verschieben"
                  >
                    <FiArrowUp size={16} />
                  </button>
                )}
                {canMoveDown && (
                  <button
                    style={{
                      background: 'rgba(255,255,255,0.9)',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      padding: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#666'
                    }}
                    onClick={handleMoveDown}
                    title="Gruppe nach unten verschieben"
                  >
                    <FiArrowDown size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        </DraggablePanelGroup>
      );
    }
    // Normales Panel
    return (
      <DraggablePanel key={panel._reactKey || panel.id} panel={panel} isAuthor={isAuthor}>
        <CollapsiblePanel
          title={panel.title}
          textColor={panel.textColor || '#000000'}
          backgroundColor={panel.backgroundColor || '#fafafa'}
          borderStyle={panel.borderStyle || 'solid'}
          borderColor={panel.borderColor || '#ddd'}
          borderThickness={panel.borderThickness || '1'}
          isAuthor={isAuthor}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          visibleWeeks={visibleWeeks}
          weekWidth={weekWidth}
          isVisibleWeeksChanging={isVisibleWeeksChanging}
          isZoomChanging={isZoomChanging}
          onInfoPanelsChange={onInfoPanelsChange}
          batchOperations={batchOperations}
          panelId={panel.id}
          open={panel.open !== undefined ? panel.open : true}
          onOpenChange={(newOpen) => {
            const updatedPanels = panels.map(p =>
              p.id === panel.id ? { ...p, open: newOpen } : p
            );
            setPanels(updatedPanels);
            if (onPanelsChange) onPanelsChange(updatedPanels);
          }}
          onSave={(newTitle, newTextColor, newBackgroundColor, newBorderStyle, newBorderColor, newBorderThickness, newMovableBars) => {
            const updatedPanels = panels.map(p =>
              p.id === panel.id 
                ? { ...p, title: newTitle, textColor: newTextColor, backgroundColor: newBackgroundColor, borderStyle: newBorderStyle, borderColor: newBorderColor, borderThickness: newBorderThickness, movableBars: newMovableBars || [] }
                : p
            );
            setPanels(updatedPanels);
            if (onPanelsChange) onPanelsChange(updatedPanels);
            
            // Prüfe nach dem Umbenennen auf Duplikate
            if (onCheckForDuplicates) {
              setTimeout(onCheckForDuplicates, 100);
            }
          }}
          movableBars={panel.movableBars || []}
          onBarsChange={(newBars) => handleBarsChange(panel.id, newBars)}
          onDelete={() => {
            // Panel löschen
            const newPanels = panels.filter(p => p.id !== panel.id);
            setPanels(newPanels);
            if (onPanelsChange) onPanelsChange(newPanels);
            
            // Prüfe nach dem Löschen auf Duplikate
            if (onCheckForDuplicates) {
              setTimeout(onCheckForDuplicates, 100);
            }
          }}
        >
          <p>Inhalt von "{panel.title}"</p>
        </CollapsiblePanel>
      </DraggablePanel>
    );
  }

  return (
    <div style={{
      borderTop: `${editBorderThickness}px ${editBorderStyle} ${editBorderColor}`,
      borderBottom: `${editBorderThickness}px ${editBorderStyle} ${editBorderColor}`,
      borderLeft: 'none',
      borderRight: 'none',
      borderRadius: 8,
      marginBottom: 16,
      background: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
    }}>
      <div
        style={{
          display: 'flex',
          alignItems: editing ? 'flex-start' : 'center',
          cursor: editing ? 'default' : 'pointer',
          padding: '12px 0px',
          fontWeight: 'bold',
          fontSize: 20,
          userSelect: 'none',
          color: editTextColor,
          minHeight: editing ? 'auto' : '48px'
        }}
        onClick={editing ? undefined : (() => {
          const newOpen = !isOpen;
          setIsOpen(newOpen);
          if (onOpenChange) {
            onOpenChange(newOpen);
          }
        })}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            left: 0,
            backgroundColor: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
            paddingRight: '8px',
            zIndex: 10
          }}>
          {!editing && (isOpen ? <FiChevronDown /> : <FiChevronRight />)}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: editing ? 'flex-start' : 'center',
            marginLeft: editing ? 0 : 0,
            flex: 1,
            justifyContent: 'space-between',
            flexDirection: editing ? 'column' : 'row'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            flex: 1, 
            width: '100%'
          }}>
            {editing ? (
              <EditStyleDialog
                editTitle={editGroupTitle}
                setEditTitle={setEditGroupTitle}
                editTextColor={editTextColor}
                setEditTextColor={setEditTextColor}
                editBackgroundColor={editBackgroundColor}
                setEditBackgroundColor={setEditBackgroundColor}
                editBorderStyle={editBorderStyle}
                setEditBorderStyle={setEditBorderStyle}
                editBorderColor={editBorderColor}
                setEditBorderColor={setEditBorderColor}
                editBorderThickness={editBorderThickness}
                setEditBorderThickness={setEditBorderThickness}
                onSave={handleSave}
                onCancel={handleCancel}
                onDelete={onDelete ? handleDelete : undefined}
                titlePlaceholder="Gruppen-Titel eingeben"
                textColorPlaceholder="#000000"
                backgroundColorPlaceholder="#f8f9fa"
              />
            ) : (
              <span style={{ 
                marginLeft: 0,
                position: 'sticky',
                left: '32px',
                backgroundColor: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
                paddingRight: '8px',
                zIndex: 9
              }}>{editGroupTitle}</span>
            )}
          </div>
          {!editing && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              position: 'sticky',
              right: 0,
              backgroundColor: applyOpacityToColor(editBackgroundColor, PANEL_OPACITY),
              paddingLeft: '8px',
              zIndex: 10
            }}>
              {/* Toggle-Icon für Batch-Auswahl aller Gruppen-Tasks */}
              {batchOperations && batchOperations.isSelectionMode && getAllGroupTaskIds().length > 0 && (
                <span
                  style={{ 
                    cursor: 'pointer', 
                    padding: '4px',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#666',
                    transition: 'background-color 0.2s',
                    marginRight: '4px'
                  }}
                  onClick={handleToggleAllGroupTasks}
                  onMouseEnter={e => e.target.style.backgroundColor = '#e0e0e0'}
                  onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                  title="Gruppen-Tasks invertieren (ausgewählte abwählen, nicht ausgewählte auswählen)"
                >
                  <FiSquare size={16} />
                </span>
              )}
              {isAuthor && showEditButtons && (canMoveUp || canMoveDown) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginRight: '4px' }}>
                  {canMoveUp && (
                    <span
                      style={{ 
                        cursor: 'pointer', 
                        padding: '4px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#666',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        onMoveUp && onMoveUp();
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#e0e0e0'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                      title="Gruppe nach oben verschieben"
                    >
                      <FiArrowUp size={16} />
                    </span>
                  )}
                  {canMoveDown && (
                    <span
                      style={{ 
                        cursor: 'pointer', 
                        padding: '4px',
                        borderRadius: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#666',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        onMoveDown && onMoveDown();
                      }}
                      onMouseEnter={e => e.target.style.backgroundColor = '#e0e0e0'}
                      onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}
                      title="Gruppe nach unten verschieben"
                    >
                      <FiArrowDown size={16} />
                    </span>
                  )}
                </div>
              )}
              {isAuthor && showEditButtons && (
                <span
                  style={{ cursor: 'pointer', padding: '4px' }}
                  onClick={e => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  title="Titel, Farben und Border bearbeiten"
                >
                  <FiEdit2 size={16} />
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {isOpen && (
        <div style={{ padding: '12px 0px' }}>
          <DroppableGroup groupId={groupId} isAuthor={isAuthor} isEmpty={panels.length === 0}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              minHeight: panels.length === 0 ? '60px' : 'auto',
              border: panels.length === 0 ? '2px dashed #ddd' : 'none',
              borderRadius: panels.length === 0 ? '8px' : '0',
              padding: panels.length === 0 ? '16px' : '0',
              alignItems: panels.length === 0 ? 'center' : 'stretch',
              justifyContent: panels.length === 0 ? 'center' : 'flex-start',
              color: panels.length === 0 ? '#999' : 'inherit',
              fontSize: panels.length === 0 ? '14px' : 'inherit',
              transition: 'all 0.2s ease'
            }}>
              {panels.length === 0 ? (
                isAuthor ? (
                  <span>Ziehen Sie Panels hierher</span>
                ) : (
                  <span style={{ fontStyle: 'italic' }}>Keine Panels vorhanden</span>
                )
              ) : (
                panels.map(renderPanel)
              )}
            </div>
          </DroppableGroup>
          {isAuthor && showEditButtons && (
            <div style={{ 
              marginTop: 16, 
              display: 'flex', 
              flexWrap: 'wrap',
              position: 'relative',
              width: '100%'
            }}>
              <div style={{
                position: 'sticky',
                left: '5px',
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                zIndex: 8
              }}>
                <button
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onClick={() => setShowPanelDialog(true)}
                >
                  <FiPlus />
                  Panel hinzufügen
                </button>
                <button
                  style={{
                    backgroundColor: '#2196f3',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onClick={() => setShowGroupDialog(true)}
                >
                  <FiFolderPlus />
                  Gruppe hinzufügen
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Panel hinzufügen Dialog */}
      {showPanelDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Neues Panel hinzufügen</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Panel-Titel:
              </label>
              <input
                type="text"
                value={newPanelTitle}
                onChange={e => setNewPanelTitle(e.target.value)}
                style={{
                  width: '80%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                placeholder="Panel-Titel eingeben..."
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAddPanel();
                  if (e.key === 'Escape') setShowPanelDialog(false);
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => setShowPanelDialog(false)}
              >
                Abbrechen
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onClick={handleAddPanel}
                disabled={newPanelTitle.trim() === ''}
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gruppe hinzufügen Dialog */}
      {showGroupDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Neue Gruppe hinzufügen</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Gruppen-Titel:
              </label>
              <input
                type="text"
                value={newGroupTitle}
                onChange={e => setNewGroupTitle(e.target.value)}
                style={{
                  width: '80%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
                placeholder="Gruppen-Titel eingeben..."
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAddGroup();
                  if (e.key === 'Escape') setShowGroupDialog(false);
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => setShowGroupDialog(false)}
              >
                Abbrechen
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onClick={handleAddGroup}
                disabled={newGroupTitle.trim() === ''}
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Löschen-Bestätigungsdialog */}
      {showDeleteDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '400px',
            maxWidth: '90vw'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#ff5722' }}>
              <FiTrash2 style={{ marginRight: '8px' }} />
              Gruppe löschen
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', lineHeight: '1.5' }}>
              Sind Sie sicher, dass Sie die Gruppe <strong>"{editGroupTitle}"</strong> und alle darin enthaltenen Panels löschen möchten?
            </p>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => setShowDeleteDialog(false)}
              >
                Abbrechen
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#ff5722',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onClick={confirmDelete}
              >
                Gruppe löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
